import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CustomCursor from '@/pages/home/components/CustomCursor';

type FormDataType = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

export default function ContactPage() {
  const [loaded, setLoaded] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(t);
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormDataType>();

  const onSubmit = async (data: FormDataType) => {
    setLoading(true);
    setError(null);

    let text = '';
    for (const [key, value] of Object.entries(data)) {
      text += `${key} - ${value}\n`;
    }

    const payload = {
      email: data.email,
      text,
      problemType: data.subject,
      to_email: 'help@syntera-solutions.com',
      subject: ' ',
    };

    try {
      const res = await fetch('https://rocknlabs.com/api/support/request_form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Server error');

      setSubmitted(true);
      reset();
    } catch {
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="w-full overflow-x-hidden bg-[#0d0d0d]">
      <CustomCursor />
      <Navbar />

      {/* Hero */}
      <section className="relative w-full min-h-screen flex items-end overflow-hidden pt-24">
        <span
          className="boldonse absolute select-none pointer-events-none"
          style={{
            fontSize: '52vw',
            lineHeight: 1,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -48%)',
            color: 'rgba(255,255,255,0.03)',
            letterSpacing: '-8px',
            whiteSpace: 'nowrap',
          }}
        >
          TALK
        </span>

        <div className="relative z-10 px-10 lg:px-20 pt-40 pb-24 max-w-3xl">
          <div className={`transition-all duration-1000 delay-200 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-[#F8CA9D] text-xs tracking-[0.5em] uppercase mb-10 font-medium">
              &#8212;&nbsp;&nbsp;Get in touch&nbsp;&nbsp;&#8212;
            </p>
            <h1
              className="boldonse text-white leading-none mb-8"
              style={{ fontSize: 'clamp(64px, 9vw, 130px)', lineHeight: 0.88 }}
            >
              CONTACT<br />
              <span style={{ color: '#FA6E4F' }}>US.</span>
            </h1>
          </div>
          <div className={`transition-all duration-1000 delay-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
            <p className="text-white/60 text-lg leading-relaxed max-w-md">
              Have a project in mind? Let&apos;s discuss how Syntera Solutions can help bring your ideas to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="px-10 lg:px-20 py-24" style={{ background: '#111' }}>
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[#8EC9BB]/60 text-xs tracking-[0.5em] uppercase mb-4">Send a message</p>
          <h2 className="boldonse text-white text-4xl mb-10">
            LET&apos;S START A<br />
            <span style={{ color: '#8EC9BB' }}>CONVERSATION.</span>
          </h2>

          {submitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-[#8EC9BB]/20 flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-[#8EC9BB] text-3xl" />
              </div>
              <p className="text-white text-xl font-medium">Message sent!</p>
              <p className="text-white/60 mt-2">We&apos;ll get back to you within 24 hours.</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-white/60 text-xs tracking-widest uppercase mb-2 block">Name</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    className="w-full bg-transparent border-b-2 border-white/20 focus:border-[#8EC9BB] outline-none text-white placeholder-white/30 text-lg py-3 transition-colors duration-300"
                    placeholder="Your name"
                  />
                  {errors.name && <p className="text-[#FA6E4F] text-xs mt-1 text-left">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="text-white/60 text-xs tracking-widest uppercase mb-2 block">Email</label>
                  <input
                    type="email"
                    {...register('email', { required: 'Email is required' })}
                    className="w-full bg-transparent border-b-2 border-white/20 focus:border-[#8EC9BB] outline-none text-white placeholder-white/30 text-lg py-3 transition-colors duration-300"
                    placeholder="your@email.com"
                  />
                  {errors.email && <p className="text-[#FA6E4F] text-xs mt-1 text-left">{errors.email.message}</p>}
                </div>
              </div>
              <div>
                <label className="text-white/60 text-xs tracking-widest uppercase mb-2 block">Subject</label>
                <input
                  type="text"
                  {...register('subject', { required: 'Subject is required', minLength: { value: 3, message: 'Subject must be at least 3 characters' } })}
                  className="w-full bg-transparent border-b-2 border-white/20 focus:border-[#8EC9BB] outline-none text-white placeholder-white/30 text-lg py-3 transition-colors duration-300"
                  placeholder="Project inquiry"
                />
                {errors.subject && <p className="text-[#FA6E4F] text-xs mt-1 text-left">{errors.subject.message}</p>}
              </div>
              <div>
                <label className="text-white/60 text-xs tracking-widest uppercase mb-2 block">Message</label>
                <textarea
                  {...register('message', { required: 'Message is required', minLength: { value: 20, message: 'Message must be at least 20 characters' } })}
                  maxLength={500}
                  rows={4}
                  className="w-full bg-transparent border-b-2 border-white/20 focus:border-[#8EC9BB] outline-none text-white placeholder-white/30 text-lg py-3 transition-colors duration-300 resize-none"
                  placeholder="Tell us about your project..."
                />
                {errors.message && <p className="text-[#FA6E4F] text-xs mt-1 text-left">{errors.message.message}</p>}
                <p className="text-white/30 text-xs mt-1">Max 500 characters</p>
              </div>
              {error && <p className="text-[#FA6E4F] text-sm">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="mt-4 boldonse px-10 py-4 rounded-full text-sm tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50"
                style={{ background: '#8EC9BB', color: '#0d0d0d' }}
              >
                {loading ? 'Sending...' : 'SEND MESSAGE'}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
